import tokens from '@lansforsakringar/core-tokens'
import { css } from 'lit'

import { vars, scoped, declare, override } from '#utils/styles'

const locals = scoped('form-boolean-card', [
	'background_color',
	'border_color',
	'color'
] as const)

export default css`
	:host {
		${declare(locals, {
			background_color: tokens.color_background_secondary,
			border_color: tokens.color_border_secondary,
			color: tokens.color_text_primary
		})};

		&:has(.input:disabled) {
			${override(locals.color, tokens.color_text_disabled)};
		}

		width: 100%;
	}

	.card {
		box-sizing: border-box;
		display: grid;
		user-select: none;
		grid-template-areas:
			'figure label control'
			'figure description control';
		position: relative;
		border: 1px solid ${vars(locals.border_color)};
		border-radius: ${vars(tokens.border_radius_sm)};
		background: ${vars(locals.background_color)};
		padding: ${vars(tokens.spacing_sm)};
		padding-inline-end: ${vars(tokens.spacing_md)};

		transition: 100ms var(--ease-in);
		transition-property: box-shadow, border-color;

		grid-template-columns: auto 1fr auto;
		align-items: center;
		column-gap: ${vars(tokens.spacing_sm)};

		&:has(input:checked) {
			${override(locals.border_color, tokens.color_border_selected)};
		}

		&:has(input:focus-visible),
		&:hover {
			${override(locals.border_color, tokens.color_border_selected)};

			box-shadow: inset 0 0 0 1px ${vars(locals.border_color)};
			transition-timing-function: var(--ease-out);
		}

		&.focus-visible:has(input:focus-visible) {
			outline: var(--focus-outline);
			outline-offset: var(--focus-outline-offset);
		}
	}

	.control {
		grid-area: control;
	}

	.figure {
		grid-area: figure;
		pointer-events: none;
	}

	.label {
		font-size: 1rem;
		font-weight: 600;
		line-height: calc(19 / 16);
		grid-area: label;
		text-wrap: balance;
		cursor: pointer;
		user-select: none;
		color: ${vars(tokens.color_text_brand_primary)};

		&::before {
			content: '';
			position: absolute;
			inset: 0;
		}
	}

	.description {
		margin: 0;
		font-size: 0.875rem;
		font-weight: normal;
		line-height: calc(18 / 14);
		grid-area: description;
		text-wrap: balance;
		max-height: calc(2em * (18 / 14));
		overflow: hidden;
		color: ${vars(tokens.color_text_secondary)};
	}

	.card *:not(::slotted(*)) {
		box-sizing: inherit;
		margin: 0;
		padding: 0;
	}

	.validation {
		display: flex;
		align-items: flex-start;
		padding-block-start: ${vars(tokens.spacing_xs)};
		cursor: default;
		gap: ${vars(tokens.spacing_xs)};
		color: ${vars(tokens.color_text_error)};
		line-height: calc(18 / 14);
		font-size: 0.875rem;
		margin-block-start: ${vars(tokens.spacing_xs)};

		.icon {
			margin: -0.2rem 0 0 -0.15rem;
		}
	}

	.vertical {
		grid-template-areas:
			'control'
			'figure'
			'label'
			'description';
		width: 100%;
		min-width: 11rem;
		padding-inline-end: ${vars(tokens.spacing_sm)};
		padding-block-end: ${vars(tokens.spacing_lg)};

		grid-template-columns: auto;
		align-items: auto;
		column-gap: ${vars(tokens.spacing_sm)};
	}

	.vertical .figure {
		justify-self: center;
		margin-block-end: ${vars(tokens.spacing_sm)};
	}

	.vertical .label {
		text-align: center;
	}

	.vertical .description {
		text-align: center;
	}

	@media (min-width: 600px) {
		.card:not(.horizontal) {
			grid-template-areas:
				'control'
				'figure'
				'label'
				'description';
			width: 100%;
			min-width: 11rem;
			padding-inline-end: ${vars(tokens.spacing_sm)};
			padding-block-end: ${vars(tokens.spacing_lg)};

			grid-template-columns: auto;
			align-items: auto;
			column-gap: ${vars(tokens.spacing_sm)};

			& .figure {
				justify-self: center;
				margin-block-end: ${vars(tokens.spacing_sm)};
			}

			& .label,
			& .description {
				text-align: center;
			}
		}
	}
`
